import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Popover,
  Card,
  Menu,
  Position,
  Icon,
  Shelf,
  Box,
  Text,
  Stack,
  Tooltip,
  Banner,
  Button,
} from "@chaser/components";
import { Link } from "react-router-dom";
import { useRouteMatch } from "react-router-dom";
import LoadingBar from "react-top-loading-bar";
import { logout } from "../../app/login/duck/actions/login";
import config from "../../config/index";
import useUserOrganisations from "../../hooks/useUserOrganisations";
import useUser from "../../hooks/useUser";
import useShortCode from "../../hooks/useShortCode";
import useOrganisation from "../../hooks/useOrganisation";
import { bool, func, object } from "prop-types";
import colors from "../../mixins/colorMap";
import { ellipsisTooltip } from "../../util";
import SyncButton from "../SyncButton";
import NotificationsPanel from "../NotificationsPanel";
import LogoutOverlay from "../LogoutOverlay";
import Logo from "../Logo";
import useResponsiveValue from "../../hooks/useResponsiveValue";
import SupportMenu from "./SupportMenu/index";
import useKeyboardShortcuts from "../../hooks/useKeyboardShortcuts";
import { useBannerContext } from "../BannerControl/BannerRenderer";
import { BannerTypes } from "../BannerControl";
import { CallStages } from "../VoiceCallingBanners";
import useTrialExpiry from "../../hooks/useTrialExpired";
import { roles } from "../../util/constants";
import DemoGate from "../FeatureGate/DemoGate";
import SwapOrganisationsMenu from "./SwapOrganisationsMenu";
import useDisplayBanner from "../../hooks/useDisplayBanner";
import RoleGate from "../FeatureGate/RoleGate";

import style from "./HeaderBar.module.scss";

const formatDate = (date) => {
  const dt = new Date(date);
  dt.setUTCHours(0, 0, 0, 0);

  return dt.toLocaleString("en-gb", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

const getOrgLabel = (noChase, noChaseUntil, activeCall) => {
  if (activeCall) return "(On call)";
  if (noChaseUntil) return `(Paused until  ${formatDate(noChaseUntil)})`;
  if (noChase) return "(Paused)";
  return "";
};

const getOrgLabelTooltip = (activeCall) => {
  if (activeCall)
    return "You currently have a phone call in progress. Switching to another organisation is not possible until the call has been ended.";
  return null;
};

const HeaderBar = ({ showLinks, switchOrgs, syncAction }) => {
  const { expiryLabel } = useTrialExpiry(true);
  const bannerCtx = useBannerContext();
  const activeCall =
    bannerCtx?.open &&
    bannerCtx?.type === BannerTypes?.voiceCall &&
    bannerCtx?.opts?.callStage === CallStages.in_progress;

  const isLargeScreen = useResponsiveValue([false, false, true]);
  const [loggingOut, setLoggingOut] = useState(false);
  const loadingBar = useRef();
  const shortCode = useShortCode();

  const {
    firstName,
    lastName,
    features,
    organisations: userOrganisations = [],
  } = useUser();
  const organisation = useOrganisation(null, {
    suspense: false,
  });
  const { noChase, noChaseUntil, sandbox } = organisation;
  const dispatch = useDispatch();
  const fetchedOrganisations = useUserOrganisations();
  const organisations = fetchedOrganisations ?? userOrganisations;
  const selected = organisations?.find((org) => org.ShortCode === shortCode);
  const { name, Name } = selected || {};
  const orgName = name || Name || organisation.name || organisation.Name || "";
  const hasOrganisations = organisations && organisations.length > 0;

  const welcomeMatch = useRouteMatch({
    path: ["/addorganisation/csvconfiguration", "/select-organisations"],
    exact: false,
  });

  const showBanner = useDisplayBanner();
  const isAllowedToSwitchOrg = hasOrganisations && switchOrgs && !activeCall;
  const labelTooltip = getOrgLabelTooltip(activeCall);

  useKeyboardShortcuts();

  return (
    <>
      <Box
        px="medium"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        height={40}
        backgroundColor="Orange"
        color="White"
      >
        <Shelf gap="large" alignItems="center">
          <Shelf gap="small">
            {isLargeScreen && (
              <Link id="brand-link" to={"/receivables/dashboard"}>
                <Logo height={15} />
              </Link>
            )}
          </Shelf>
          {isAllowedToSwitchOrg ? (
            <SwapOrganisationsMenu
              noChase={noChase}
              noChaseUntil={noChaseUntil}
            />
          ) : (
            <Tooltip
              content={labelTooltip}
              disable={!labelTooltip}
              position={Position.BOTTOM_RIGHT}
            >
              <Text
                color="White"
                disabled
                trim={false}
                className={style.orgName}
              >
                {orgName} {getOrgLabel(noChase, noChaseUntil, activeCall)}{" "}
              </Text>
            </Tooltip>
          )}
        </Shelf>
        <Shelf>
          {orgName && showLinks && (
            <>
              {isLargeScreen && (
                <SyncButton
                  onSyncStart={() => loadingBar.current?.continousStart(20)}
                  onSyncFinish={() => loadingBar.current?.complete()}
                  syncAction={syncAction}
                />
              )}
              <NotificationsPanel shortCode={shortCode} />
            </>
          )}
          <SupportMenu />
          <Popover
            position={Position.BOTTOM_RIGHT}
            target={
              <Icon
                data-testid="settings-menu"
                data-trackingid="settings-menu"
                name="user"
                size={24}
                data-tour="settings-menu"
              />
            }
          >
            <Card>
              <Menu>
                <Menu.Item noHover>
                  <Stack gap={0}>
                    <Text trim={false}>
                      {firstName} {lastName || ""}
                    </Text>
                    {orgName ? (
                      <Text trim={false} color="Mid Neutral 1">
                        <>{ellipsisTooltip(orgName, 25, "bottom")}</>
                      </Text>
                    ) : null}
                  </Stack>
                </Menu.Item>
                <Menu.Divider />
                <RoleGate roles={[roles.admin]}>
                  <Menu.Item as={Link} to="/admin">
                    Admin
                  </Menu.Item>
                </RoleGate>
                {features?.isPartnerHubUser && (
                  <Menu.Item
                    as="a"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={config.partnerHub}
                  >
                    Partner Hub
                  </Menu.Item>
                )}
                {!welcomeMatch && (
                  <>
                    <Menu.Item as={Link} to="/user">
                      Account settings
                    </Menu.Item>
                    {orgName ? (
                      <Menu.Item
                        as={Link}
                        to="/settings"
                        data-testid="settings-link"
                        data-trackingid="settings-link"
                        data-tour="settings-link"
                      >
                        Organisation settings
                      </Menu.Item>
                    ) : null}
                  </>
                )}
                <Menu.Item
                  onClick={() => {
                    setLoggingOut(true);
                    dispatch(logout()).finally(() => {
                      setLoggingOut(false);
                    });
                  }}
                >
                  Logout
                </Menu.Item>
              </Menu>
            </Card>
          </Popover>
        </Shelf>
      </Box>
      {showBanner && (
        <Banner
          className={style.trialBannerDrm}
          justifyContent="center"
          showCloseButton={false}
          backgroundColor="Dark Neutral 1"
          borderColor="transparent"
          intent="info"
          borderRadius="0"
          icon={<Icon name="alert-square" size={16} />}
        >
          <Shelf>
            {expiryLabel}{" "}
            {sandbox ? (
              <DemoGate>
                <Button size="narrow" intent="positive">
                  Connect now
                </Button>
              </DemoGate>
            ) : (
              <Button
                size="narrow"
                intent="positive"
                as={Link}
                to={`/settings/subscription/addons`}
              >
                Subscribe now
              </Button>
            )}
          </Shelf>
        </Banner>
      )}
      <LoadingBar
        ref={loadingBar}
        className={style.loadingBar}
        height={0}
        color={colors["Mid Neutral 3"]}
      />
      {loggingOut && <LogoutOverlay />}
    </>
  );
};

HeaderBar.propTypes = {
  showLinks: bool,
  syncAction: object,
};

export default HeaderBar;
export { getOrgLabel };
